/**
 * @group Utils
 * 
 * @summary
 * <blockquote>Get the appropriate PostHog instance based on environment</blockquote>
 * 
 * This utility detects whether the code is running in a browser or Node.js environment
 * and returns the appropriate PostHog client instance.
 * 
 * @example
 * ```typescript
 * import { getPostHogInstance } from '@shared/reporting';
 * 
 * const posthog = await getPostHogInstance(apiKey, host);
 * await posthog.capture(event);
 * ```
 */

import { tryCatchAsync, ok, err, AsyncResult, AppError, getEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
import { POSTHOG_CONFIG, POSTHOG_MESSAGES } from '../constants';
import { formatMessage } from './formatMessage';
import { Logger } from '@shared/logger';

/**
 * @summary
 * <blockquote>Get PostHog instance for current environment</blockquote>
 * 
 * Detects browser vs Node.js environment and returns appropriate PostHog client.
 * 
 * @param apiKey - PostHog API key
 * @param host - PostHog host URL
 * @returns AsyncResult with PostHog client instance
 */
/**
 * @internal
 */
export async function getPostHogInstance(): AsyncResult<any, AppError> {
  const isServerSide = typeof window === 'undefined';

  const result = await tryCatchAsync(async () => {
    // Get API key and host from environment (try both web and mobile env var names)
    let finalApiKey = getEnvironmentValue(ENVIRONMENT_VALUES.ANALYTICS.NEXT_PUBLIC_POSTHOG_KEY).match({
      ok: (value) => value,
      err: () => null
    });
    
    // Fallback to mobile env var if web env var not found
    if (!finalApiKey) {
      finalApiKey = getEnvironmentValue(ENVIRONMENT_VALUES.ANALYTICS.EXPO_PUBLIC_POSTHOG_KEY).match({
        ok: (value) => value,
        err: () => null
      });
    }

    let finalHost = getEnvironmentValue(ENVIRONMENT_VALUES.ANALYTICS.NEXT_PUBLIC_POSTHOG_HOST).match({
      ok: (value) => value,
      err: () => null
    });
    
    // Fallback to mobile env var if web env var not found
    if (!finalHost) {
      finalHost = getEnvironmentValue(ENVIRONMENT_VALUES.ANALYTICS.EXPO_PUBLIC_POSTHOG_HOST).match({
        ok: (value) => value,
        err: () => POSTHOG_CONFIG.DEFAULT_HOST
      });
    }
    
    // Use default host if still not found
    if (!finalHost) {
      finalHost = POSTHOG_CONFIG.DEFAULT_HOST;
    }

    if (!finalApiKey) {
      return err(null);
    }

    // Check if we're in a browser environment
    if (!isServerSide) {
      // Detect React Native vs Browser
      const isReactNative = typeof navigator !== 'undefined' && 
                           typeof document === 'undefined' &&
                           // @ts-ignore - React Native specific
                           typeof navigator.product === 'string' && 
                           // @ts-ignore
                           navigator.product === 'ReactNative';
      
      let posthog: any;
      
      // Use posthog-js for both browser and React Native
      // posthog-js works in React Native and doesn't require platform-specific code
      posthog = (await import('posthog-js')).default;
      
      // Initialize PostHog (returns the posthog instance)
      posthog.init(finalApiKey, {
        api_host: finalHost,
        // Disable session recording to avoid the onRemoteConfig error
        disable_session_recording: true,
        autocapture: false,
      });

      return posthog;
    } else {
      // Node.js environment - use posthog-node
      Logger.info(POSTHOG_MESSAGES.INITIALIZING_SERVER);

      const { PostHog } = await import('posthog-node');
      const client = new PostHog(finalApiKey, {
        host: finalHost
      });

      Logger.info(POSTHOG_MESSAGES.SERVER_INIT_SUCCESS);
      Logger.info(POSTHOG_MESSAGES.SERVER_READY);

      return client;
    }
  });

  return result.match({
    ok: async (client) => {
      if (isServerSide) {
        
        Logger.info(POSTHOG_MESSAGES.INSTANCE_READY);
      }
      return ok(client);
    },
    err: async (error) => {
      return err(error);
    }
  });
} 